import { graphql, Link, StaticQuery } from 'gatsby';
import { styled } from '@mui/material/styles';
import { GatsbyImage } from 'gatsby-plugin-image';
import { cn } from '../../lib/helpers';
import React from 'react';
import AppBar from '@mui/material/AppBar';
import IconButton from '@mui/material/IconButton';
import Toolbar from '@mui/material/Toolbar';
import useScrollTrigger from '@mui/material/useScrollTrigger';
import Drawer from '@mui/material/Drawer';
import ClickAwayListener from '@mui/material/ClickAwayListener';
import * as styles from './header.module.css';
import MenuLink from './menu-link';
import SearchIcon from '@mui/icons-material/Search';
import CancelIcon from '@mui/icons-material/Cancel';

const PREFIX = 'Header';

const classes = {
  root: `${PREFIX}-root`,
  title: `${PREFIX}-title`,
  appBar: `${PREFIX}-appBar`,
  appBarBackground: `${PREFIX}-appBarBackground`,
  drawer: `${PREFIX}-drawer`,
  drawerInner: `${PREFIX}-drawerInner`,
};

const Root = styled('div')(({ theme }) => ({
  [`& .${classes.root}`]: {
    flexGrow: 1,
  },

  [`& .${classes.title}`]: {
    flexGrow: 1,
  },

  [`& .${classes.appBar}`]: {
    [theme.breakpoints.up('sm')]: {
      width: '100%',
    },
    backgroundColor: 'transparent',
    zIndex: theme.zIndex.drawer + 1,
    boxShadow: 'none',
  },

  [`& .${classes.appBarBackground}`]: {
    backgroundColor: 'var(--color-dark-black-bg)',
    transition: 'background-color 200ms linear',
  },

  [`& .${classes.drawer}`]: {
    '& > div': {
      border: 'none',
    },
    backgroundColor: 'var(--color-dark-black-bg)',
  },

  [`& .${classes.drawerInner}`]: {
    backgroundColor: 'var(--color-dark-black-bg)',
  },
}));

const Header = ({
  fixedNav = false,
  smallHeader = false,
  siteTitle,
  siteSubtitle,
  heroImageCaption,
  backgroundImage,
}) => {
  const [drawerOpen, setDrawerOpen] = React.useState(false);
  const [searchBarVisible, setSearchBarVisible] = React.useState(false);
  const [isEditing, setEditing] = React.useState(false);
  const navDrawerRef = React.useRef(null);
  const menuIconRef = React.useRef(null);

  // console.log("backgroundImage: ", backgroundImage)
  const toggleEditing = () => {
    setEditing(!isEditing);
    setSearchBarVisible(!isEditing);
  };

  const isBrowserApi = () => typeof window !== 'undefined' ? true : false;

  React.useEffect(() => {
    if (isEditing) {
      inputRef.current.focus();
    }

    /* 
      Accessiblity issue 30702: if Nav drawer is open, then focus must stay in the drawer when user navigates inside the nav using Tab and Shift-Tab, i.e.:
      1. When the last interactive element (a link) in the Nav has focus and user clicks the Tab key, focus must be set to the first interactive element (menu button) in the Nav and not something outside the Nav
      2. When the first interactive element (menu button) in the Nav has focus and user clicks on Shift-Tab, focus must be set to the last interactive element (a link) in the Nav and not something outside the Nav
    */
    if (drawerOpen) {
      const navDrawerLinks = navDrawerRef.current.querySelectorAll('a');
      const navDrawerFirstLink = navDrawerLinks.length ? navDrawerLinks[0] : null;
      const navDrawerLastLink = navDrawerLinks.length ? navDrawerLinks[navDrawerLinks.length - 1] : null;
      const menuIcon = menuIconRef.current;
      
      // define what to use for the first and last items in the Nav
      const navDrawerFirstItem = menuIcon; // or navDrawerFirstLink
      const navDrawerLastItem = navDrawerLastLink;

      /* if focus is currently on first nav item, Tab key press will focus on last nav item */
      const handleNavFirstItemFocus = (e) => {
        if (e.shiftKey && e.key === 'Tab') {
          e.preventDefault();
          navDrawerLastItem.focus();
        }
      };

       /* if focus is currently on last nav item, Shift-Tab key press will focus on first nav item */
      const handleNavLastItemFocus = (e) => {
        // very important too include that Shift key not pressed while Tab key is pressed; 
        // otherwise hitting Tab with Shift will fire this event handler and cause weird issues
        if (!e.shiftKey && e.key === 'Tab') {
          e.preventDefault();
          navDrawerFirstItem.focus();
        }
      };
    
      navDrawerFirstItem && navDrawerFirstItem.addEventListener('keydown', handleNavFirstItemFocus);
      navDrawerLastItem && navDrawerLastItem.addEventListener('keydown', handleNavLastItemFocus);
      
      return () => {
        navDrawerFirstItem && navDrawerFirstItem.removeEventListener('keydown', handleNavFirstItemFocus);
        navDrawerLastItem && navDrawerLastItem.removeEventListener('keydown', handleNavLastItemFocus);
      }
    }
  }, [isEditing, drawerOpen]);


  const inputRef = React.useRef(null);

  // console.log("classes: ", classes);
  const escFunction = (event) => {
    if (isEditing && event.keyCode === 27) {
      toggleEditing();
    }
  };

  const trigger = useScrollTrigger({
    disableHysteresis: true,
    threshold: 300,
  });
  return (
    // eslint-disable-next-line react/jsx-no-undef
    <StaticQuery
      query={graphql`
        {
          logo: file(relativePath: { eq: "icon-logo.png" }) {
            childImageSharp {
              gatsbyImageData(width: 400, quality: 100, placeholder: NONE, layout: CONSTRAINED)
            }
          }
          schools: allSanitySchool {
            nodes {
              ...School
            }
          }
          mainMenu: sanityMenu(slug: { current: { eq: "main-menu" } }) {
            title
            hidden
            links {
              _key
              title
              href
              hidden
            }
          }
          about: sanityMenu(slug: { current: { eq: "about" } }) {
            title
            hidden
            links {
              _key
              title
              href
              hidden
            }
          }
          galleries: sanityMenu(slug: { current: { eq: "galleries" } }) {
            title
            hidden
            links {
              _key
              title
              href
              hidden
            }
          }
          pastShows: sanityMenu(slug: { current: { eq: "past-shows" } }) {
            title
            hidden
            links {
              _key
              title
              href
              hidden
            }
          }
        }
      `}
      render={({
        logo,
        schools,
        about,
        about: { links: aboutLinks },
        galleries,
        galleries: { links: galleryLinks },
        mainMenu,
        mainMenu: { links: mainMenuLinks },
        pastShows,
        pastShows: { links: pastShowLinks },
      }) => {
        const displaySchools = schools.nodes.sort((a, b) => a.title.localeCompare(b.title));
        // split past show links into two columns (but hide the hidden ones first)
        const pastShowLinks2 = [...pastShowLinks.filter(({ hidden }) => hidden !== true)];
        const pastShowLinks1 = pastShowLinks2.splice(0, Math.ceil(pastShowLinks2.length / 2));
        // hack to show/hide schools since this option is not available in the CMS
        const isDisplaySchools = true;
        // console.log("logo: ", logo);
        // console.log("backgroundImage: ", backgroundImage);

        return (
          <Root className={styles.root}>
            <div className="st-search-container" />
            <ClickAwayListener onClickAway={() => (drawerOpen ? setDrawerOpen(!drawerOpen) : null)}>
              <div>
                <AppBar className={cn(classes.appBar, (fixedNav || trigger || drawerOpen) && classes.appBarBackground)}>
                  <Toolbar className={styles.toolbar} disableGutters>
                    <div className={styles.branding}>
                      <GatsbyImage
                        image={logo?.childImageSharp?.gatsbyImageData}
                        className={styles.brandingImage}
                        loading="eager"
                        alt="Academy of Art University"
                      />
                      <Link className={styles.navLink} to="https://www.academyart.edu/">
                        <span className={styles.srOnly}>Go to home page</span>
                      </Link>
                    </div>
                    {/* <form className={styles.searchWrapper}>
                      <IconButton
                        color="inherit"
                        style={{ display: searchBarVisible ? 'none' : 'block' }}
                        aria-label="search"
                        onClick={toggleEditing}
                      >
                        <SearchIcon style={{ fontSize: 35 }} />
                      </IconButton>
                      <IconButton
                        color="inherit"
                        aria-label="search"
                        style={{ display: searchBarVisible ? 'block' : 'none' }}
                        onClick={toggleEditing}
                      >
                        <CancelIcon style={{ fontSize: 35 }} />
                      </IconButton>
                      <div className={cn(styles.searchBar, searchBarVisible && styles.searchBarVisible)}>
                        <input type="text" onKeyDown={escFunction} ref={inputRef} className="st-default-search-input" />
                      </div>
                    </form> */}
                    <IconButton
                      color="inherit"
                      aria-label="menu"
                      onClick={() => setDrawerOpen(!drawerOpen)}
                      size="large"
                      ref={menuIconRef}
                      className={styles.menuIconFocus}
                    >
                      <div className={cn(styles.navBurgerIcon, drawerOpen && styles.navBurgerIconOpen)}>
                        <span></span>
                        <span></span>
                        <span></span>
                        <span></span>
                      </div>
                    </IconButton>
                  </Toolbar>
                </AppBar>
                {/* leave this SHIM to push down content when fixedNav at top */}
                {fixedNav && <Toolbar />}
                <Drawer classes={{ root: classes.drawer }} variant={'persistent'} anchor="top" open={drawerOpen}>
                  <div className={classes.drawerInner}>
                    <nav className={styles.headerMenu} ref={navDrawerRef}>
                      {/*<div className={cn(styles.searchBarMobile)}>*/}
                      {/*  <input type="text" onKeyDown={escFunction} ref={inputRef} className="st-default-search-input" />*/}
                      {/*</div>*/}

                      <div className={cn(styles.headerMenuSchools, styles.noFlex, styles.marginRight)}>
                        <Link to="/" title="Spring Show Home" className={cn(styles.headerMenuTitle, styles.ssHome)}>Spring Show Home</Link>
                      </div>

                      {(isDisplaySchools || (galleries && !galleries.hide)) && (
                        <div className={styles.headerMenuContent}>
                          {/* Display Schools based on `isDisplaySchools` flag */}
                          {isDisplaySchools && displaySchools && (
                            <div className={cn(styles.headerMenuSchools, styles.flexThree)}>
                              <div>
                                <div className={styles.headerMenuTitle}>Schools</div>
                                <ul>
                                  {displaySchools.slice(0, 7).map((school) => (
                                    <li key={school.id}>
                                      <Link className={styles.navLink} to={`/schools/${school.slug.current}`} onClick={() => setDrawerOpen(false)}>
                                        {school.title}
                                      </Link>
                                    </li>
                                  ))}
                                </ul>
                              </div>
                              <div className={styles.headerMenuColumnNoTitle}>
                                <ul>
                                  {displaySchools.slice(7, 14).map((school) => (
                                    <li key={school.id}>
                                      <Link className={styles.navLink} to={`/schools/${school.slug.current}`} onClick={() => setDrawerOpen(false)}>
                                        {school.title}
                                      </Link>
                                    </li>
                                  ))}
                                </ul>
                              </div>
                              <div className={styles.headerMenuColumnNoTitle}>
                                <ul>
                                  {displaySchools &&
                                    displaySchools.slice(14).map((school) => (
                                      <li key={school.id}>
                                        <Link
                                          className={styles.navLink}
                                          to={`/schools/${school.slug.current}`}
                                          onClick={() => setDrawerOpen(false)}
                                        >
                                          {school.title}
                                        </Link>
                                      </li>
                                    ))}
                                </ul>
                              </div>
                            </div>
                          )}

                          {/* Display Galleries if desired */}
                          {galleries && !galleries.hidden && galleryLinks && (
                            <div
                              className={cn(
                                styles.headerMenuSchools,
                                isDisplaySchools && displaySchools ? styles.flexCenter : null
                              )}
                            >
                              <div>
                                <div className={styles.headerMenuTitle}>Galleries</div>
                                <ul>
                                  {galleryLinks &&
                                    galleryLinks.map(({ _key, title, href, hidden }) => (
                                      <li key={_key}>
                                        <Link className={styles.navLink} to={href} onClick={() => setDrawerOpen(false)}>
                                          {title}
                                        </Link>
                                      </li>
                                    ))}
                                </ul>
                              </div>
                            </div>
                          )}
                        </div>
                      )}
                      {/* Display Main Menu if desired */}
                      {mainMenu && !mainMenu.hidden && mainMenuLinks && (
                        <div className={styles.headerMenuCustomLinks}>
                          <div className={styles.headerMenuCustomLinksColumn}>
                            {mainMenuLinks.slice(0, 5).map(({ _key, title, href, hidden }) => (
                              <div className={styles.headerMenuTitleBottom} key={_key}>
                                <MenuLink className={styles.navLink} href={href} title={title} hidden={hidden} />
                              </div>
                            ))}
                          </div>
                          <div className={styles.headerMenuCustomLinksColumn}>
                            {mainMenuLinks.slice(5, 10).map(({ _key, title, href, hidden }) => (
                              <div className={styles.headerMenuTitleBottom} key={_key}>
                                <MenuLink className={styles.navLink} href={href} title={title} hidden={hidden} />
                              </div>
                            ))}
                          </div>
                        </div>
                      )}
                      {/* Display other Menus */}
                      <div className={cn(styles.headerMenuContent, styles.justifyNormal)}>
                        {/* Display About if desired */}
                        {about && !about.hidden && aboutLinks && (
                          <div className={cn(styles.headerMenuSchools, styles.noFlex, styles.marginRight)}>
                            <div>
                              <div className={styles.headerMenuTitle}>About</div>
                              <ul>
                                {aboutLinks.map(({ _key, title, href, hidden }) => (
                                  <li key={_key}>
                                    <Link className={styles.navLink} to={href} onClick={() => setDrawerOpen(false)}>
                                      {title}
                                    </Link>
                                  </li>
                                ))}
                              </ul>
                            </div>
                          </div>
                        )}
                        {/* Display Past Shows if desired */}
                        {pastShows && !pastShows.hidden && (pastShowLinks1 || pastShowLinks2) && (
                          <div
                            className={cn(
                              styles.headerMenuSchools,
                              styles.noFlex,
                              styles.justifyNormal,
                              styles.flexColumn
                            )}
                          >
                            <div className={cn(styles.headerMenuTitle, styles.pastShowsMenuTitle)}>
                              Past Spring Shows
                            </div>
                            <div className={styles.pastShowsMenus}>
                              <ul>
                                {pastShowLinks1 &&
                                  pastShowLinks1.map(({ _key, title, href, hidden }) => (
                                    <li key={_key}>
                                      <MenuLink className={styles.navLink} href={href} title={title} hidden={hidden} />
                                    </li>
                                  ))}
                              </ul>
                              <ul>
                                {pastShowLinks2 &&
                                  pastShowLinks2.map(({ _key, title, href, hidden }) => (
                                    <li key={_key}>
                                      <MenuLink className={styles.navLink} href={href} title={title} hidden={hidden} />
                                    </li>
                                  ))}
                              </ul>
                            </div>
                          </div>
                        )}
                      </div>
                    </nav>
                  </div>
                </Drawer>
              </div>
            </ClickAwayListener>
            {backgroundImage && (
              <div className={cn(styles.mainImage, smallHeader && styles.smallMainImage)}>
                <GatsbyImage
                  image={backgroundImage.childImageSharp?.gatsbyImageData}
                  className={styles.hero}
                  loading="eager"
                  alt="Academy of Art University"
                />
                {siteTitle && <h3 className={styles.heroTitle}>{siteTitle}</h3>}
                {siteSubtitle && <h1 className={styles.title}>{siteSubtitle}</h1>}
                {heroImageCaption && <figcaption className={styles.heroImageCaption}>{heroImageCaption}</figcaption>}
              </div>
            )}
          </Root>
        );
      }}
    />
  );
};

export default Header;
